/**
 * What landed (`equipment-page-landed`, R2–R4; frame 04, pin 1): *214 assets are in*, with the
 * register's counts as the same tags the filter chips carry, so the eye goes straight from summary
 * to chip.
 *
 * A page composition of design-system parts (the epic lists *the summary card* among them): the
 * `Card` in its frost look, frame 04's mint check disc (`IconDisc` `done`), and `Tag`s with a count,
 * each in its state's status role (a done state's tag has no dot). The tags are not controls — the chips are the filter.
 *
 * The title speaks for the upload (the import record's own counts); the tags speak for the register
 * as it is now (the ONE identify model), plus *Renamed* from the record — so as groups are
 * identified the tags move with the chips.
 *
 * An ADOPTION import (`record.mode === 'adopt'`, `equipment-page-adoption`, R8; frame 09) reads
 * differently: *Adopting 186 assets*, *BIDs kept · references joined on BID*, and where the
 * references came from — *From BI* and *From the file* (information), *To look up* (work to do), from the
 * record — with *Doesn't fit* (a problem) from the model, so a Keep moves it. A count the record does not carry
 * (null: an older record) hides its tag rather than reading 0.
 *
 * An UPDATED REGISTER (`equipment-page-reimport`, R5–R8, R12; frame 10) — a record that updated rows
 * or left register rows out — reads *Register updated*, *Matched on Asset Identifier · hand-chosen
 * products kept*, with *Updated*, *Added* and *Not in this file* from the record. Below the head, the
 * rows the file left out and nobody has decided on yet, each to *Keep* (written to the record) or
 * *Remove* (the asset card's own removal, `EquipmentAssetRemove`); *Keep All* sits beside *Undo
 * This Import*. Lines the importer refused because the register moved under the check are one line,
 * never a list (R12), on every form.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, Icon, Tag, useToast, type TagTone } from '@repo/design-system';
import { fontSans, ink, semantic, space, typeScale, weight } from '@repo/design-tokens';
import { IconDisc, visuallyHidden } from '@repo/ui';
import {
  IDENTITY_STATE_LABELS,
  linesText,
  type AssetIdentityState,
  type IdentifyModel,
} from '@repo/front-end-utils';
import type {
  TwinAssetImportAbsentRow,
  TwinAssetImportRefusedLine,
  TwinAssetImportSummary,
} from '@repo/front-end-services';

import { EquipmentAssetRemove, type AssetRemoval } from './EquipmentAssetRemove';
import { equipmentTagLook } from './equipmentFilter';
import { LateRefusalStatus } from './LateRefusalStatus';

const { roles } = semantic.status;

/** What *Keep* or *Keep All* asks the record to keep (`equipment-page-reimport`, R7, R10). */
export type AbsentKeepRequest = { ids: number[] } | { all: true };

/**
 * The *Not in this file* list's decisions (`equipment-page-reimport`, R6–R8), bound by the page to
 * the record on screen. Absent: no list is drawn, as before.
 */
export interface AbsentDecisions {
  /** Write `kept` on the record's entries; resolves once the server has, rejects with its sentence. */
  keep: (request: AbsentKeepRequest) => Promise<void>;
  /** The asset card's removal steps, so a placed row comes off the plan first (R8). */
  removal: AssetRemoval;
  /** A row was removed (or found gone): the page reads the summary again. */
  onRemoved: (notice: string) => void;
}

export interface EquipmentLandedSummaryProps {
  record: TwinAssetImportSummary;
  model: Pick<IdentifyModel, 'counts'>;
  /** The summary's right-hand actions — `equipment-page-undo-import` puts *Undo This Import* here. */
  actions?: React.ReactNode;
  absent?: AbsentDecisions;
}

/** The most *Not in this file* rows drawn; *Keep All* keeps the rest too (R6, reading 2). */
export const ABSENT_LIST_LIMIT = 50;

/**
 * R12's line, shared by the summary and the start screen an upload that wrote nothing returns to:
 * how many lines the importer refused after the check, and which — never their values. On the start
 * screen nothing was written, so there is no import to undo: it just asks for the file again.
 */
export const lateRefusalLine = (
  refused: readonly TwinAssetImportRefusedLine[] | null | undefined,
  where: 'summary' | 'start' = 'summary',
): string | null => {
  if (!refused || refused.length === 0) return null;
  const n = refused.length;
  return (
    `${n} ${n === 1 ? 'line wasn’t' : 'lines weren’t'} written (${linesText(
      refused.map(entry => entry.line),
    )}): the register changed while you were checking. ` +
    (where === 'start' ? 'Upload the file again.' : 'Undo This Import, then upload the file again.')
  );
};

/**
 * Which form the summary takes (R5): *Register updated* when the record updated rows or left any
 * register row out, decided or not. The stored *Not in this file* total counts too, so the form
 * survives the rows left out being removed from the register (the list then drops them, R10).
 */
export const isRegisterUpdate = (record: TwinAssetImportSummary): boolean =>
  (record.counts?.updated ?? 0) > 0 ||
  (record.counts?.absent ?? 0) > 0 ||
  (record.absent ?? []).length > 0;

/** Frame 04's order: the done states first, then what is left. */
const STATE_ORDER: readonly AssetIdentityState[] = [
  'brand',
  'to_confirm',
  'generic',
  'to_identify',
  'not_tracked',
];

/*
 * A plain row INSIDE the card rather than `styled(Card)`: a styled wrapper is built when this module
 * loads, and the page suites that stub the design system with only what they render would hand it
 * an undefined `Card`.
 */
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${space[3]};
  font-family: ${fontSans};

  .summary-text {
    flex: 0 1 auto;
    min-width: 0;
  }
  .summary-title {
    margin: 0;
    color: ${ink[1]};
    font-size: ${typeScale.subtitle2.fontSize};
    line-height: ${typeScale.subtitle2.lineHeight};
    font-weight: ${weight.semibold};
  }
  .summary-line {
    margin: 0;
    color: ${ink[3]};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
  }
  .summary-tags {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: ${space[1]};
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .summary-actions {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: ${space[2]};
  }
`;

/** The late-refusal line and the list sit under the head, inside the same card. */
const Below = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  margin-top: ${space[2]};
  font-family: ${fontSans};

  .absent-list {
    display: flex;
    flex-direction: column;
    gap: ${space[1]};
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .absent-row {
    display: flex;
    align-items: center;
    gap: ${space[3]};
  }
  .absent-text {
    flex: 1;
    min-width: 0;
    margin: 0;
    color: ${ink[2]};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
  }
  .absent-name {
    color: ${ink[1]};
    font-weight: ${weight.semibold};
  }
  .absent-actions {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: ${space[1]};
  }
  .absent-more {
    margin: 0;
    color: ${ink[3]};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
  }
`;

/** A name or an Asset Identifier the record holds as null reads as the table's em dash. */
const orDash = (value: string | null) => (value && value.trim() ? value : '—');

/** What a row's buttons call it: its name, else its Asset Identifier. */
const rowLabel = (row: TwinAssetImportAbsentRow) =>
  row.name?.trim() || row.identifier?.trim() || 'this asset';

/** *Not on plan*, or *On plan · Ground floor › Cardio*; nothing while the plan is not known. */
const whereText = (removal: AssetRemoval, id: number): string | null => {
  if (!removal.isPlacementKnown(id)) return null;
  if (removal.placementsFor(id).length === 0) return 'Not on plan';
  const where = removal.whereFor(id);
  return where.length > 0 ? `On plan · ${where.join(', ')}` : 'On plan';
};

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** A record count the summary may show: a number, never null or absent read as 0. */
const known = (n: number | null | undefined): n is number => typeof n === 'number';

/** What the *Not in this file* list hands the card to draw. */
interface AbsentListView {
  /** *Keep All {n}*, for the head's actions, ahead of *Undo This Import* (Block A's tab order). */
  keepAll: React.ReactNode;
  list: React.ReactNode;
  announced: string;
  actionsRef: React.RefObject<HTMLDivElement | null>;
  titleRef: React.RefObject<HTMLParagraphElement | null>;
}

interface SummaryCardProps {
  record: TwinAssetImportSummary;
  model: Pick<IdentifyModel, 'counts'>;
  actions?: React.ReactNode;
  absentList?: AbsentListView;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ record, model, actions, absentList }) => {
  const counts = record.counts ?? {};
  const landed = (counts.created ?? 0) + (counts.updated ?? 0);
  const renamed = counts.renamed ?? 0;
  const byState = model.counts.byState;
  const tags = STATE_ORDER.filter(state => byState[state] > 0);
  // The lines left out on purpose (`equipment-page-upload-values`, R16), read from the record so
  // the sentence survives a reload. An older record has none.
  const leftOut = record.leftOut ?? [];
  const adopting = record.mode === 'adopt';
  const updatedForm = isRegisterUpdate(record);
  const late = lateRefusalLine(record.refused);
  const doesntFit = model.counts.doesntFit ?? 0;
  // Where each reference came from is information; what is left to look up is work to do; a
  // product that does not fit is a problem (`onboarding-status-colours-equipment`, R3, R4).
  const adoptionTags: { label: string; tone: TagTone; n: number }[] = [
    ...(known(record.fromBi)
      ? [{ label: 'From BI', tone: roles.information.tagTone, n: record.fromBi }]
      : []),
    ...(known(record.fromFile)
      ? [{ label: 'From the file', tone: roles.information.tagTone, n: record.fromFile }]
      : []),
    ...(known(record.toLookUp)
      ? [{ label: 'To look up', tone: roles.workToDo.tagTone, n: record.toLookUp }]
      : []),
    ...(doesntFit > 0 ? [{ label: 'Doesn’t fit', tone: roles.problem.tagTone, n: doesntFit }] : []),
  ];
  /*
   * Frame 10's counts, each only above 0. *Not in this file* counts every row left out, decided or
   * not, from the stored total, so a Keep does not move it; an older record without the total shows
   * no tag (R5). Frame 10 draws it in lavender, which no status role carries: `information`
   * (reading 1).
   */
  const updateTags: { label: string; tone: TagTone; n: number }[] = updatedForm
    ? [
        { label: 'Updated', tone: roles.information.tagTone, n: counts.updated ?? 0 },
        { label: 'Added', tone: roles.justChanged.tagTone, n: counts.created ?? 0 },
        { label: 'Not in this file', tone: roles.information.tagTone, n: counts.absent ?? 0 },
      ].filter(tag => tag.n > 0)
    : [];
  const keepAll = absentList?.keepAll;

  return (
    <Card
      surface="on-glass"
      size="sm"
      /* `selected` for its LOOK only — frame 04's frost summary surface. No selection semantics:
         Card emits no aria for it. A named Card variant replaces this once Rob ratifies the reading
         (PROVENANCE, card 11 row groups, reading 5); do not copy the pattern. */
      selected
      role="region"
      aria-label="Import summary"
      className="equipment-landed-summary"
      style={{ marginBottom: space[3] }}
    >
      <Row>
        {adopting ? (
          <IconDisc tone="draft" size={32}>
            <Icon name="arrows-rotate" />
          </IconDisc>
        ) : updatedForm ? (
          <IconDisc tone="done" size={32}>
            <Icon name="file-arrow-up" />
          </IconDisc>
        ) : (
          <IconDisc tone="done" size={32}>
            <Icon name="circle-check" variant="solid" />
          </IconDisc>
        )}
        <div className="summary-text">
          {/* Focusable by script only: where focus goes when the last decision empties the list
              and no *Undo This Import* is offered. */}
          <p className="summary-title" tabIndex={-1} ref={absentList?.titleRef}>
            {updatedForm
              ? 'Register updated'
              : adopting
                ? `Adopting ${count(landed, 'asset', 'assets')}`
                : `${count(landed, 'asset is', 'assets are')} in`}
          </p>
          {leftOut.length > 0 && (
            <p className="summary-line">
              {`${count(leftOut.length, 'row was', 'rows were')} left out of this file: ` +
                linesText(leftOut.map(entry => entry.line))}
            </p>
          )}
          <p className="summary-line">
            {/* An adoption over a register keeps its own line: its rows join on BID (reading 6). */}
            {adopting
              ? 'BIDs kept · references joined on BID'
              : updatedForm
                ? 'Matched on Asset Identifier · hand-chosen products kept'
                : `${byState.brand} identified already, from manufacturer and model`}
          </p>
        </div>
        <ul className="summary-tags">
          {updateTags.map(tag => (
            <li key={tag.label}>
              <Tag surface="on-glass" size="dense" tone={tag.tone} dot count={tag.n}>
                {tag.label}
              </Tag>
            </li>
          ))}
          {adopting &&
            adoptionTags.map(tag => (
              <li key={tag.label}>
                <Tag surface="on-glass" size="dense" tone={tag.tone} dot count={tag.n}>
                  {tag.label}
                </Tag>
              </li>
            ))}
          {!adopting &&
            tags.map(state => (
              <li key={state}>
                <Tag
                  surface="on-glass"
                  size="dense"
                  {...equipmentTagLook(state)}
                  count={byState[state]}
                >
                  {IDENTITY_STATE_LABELS[state]}
                </Tag>
              </li>
            ))}
          {!adopting && renamed > 0 && (
            <li>
              <Tag
                surface="on-glass"
                size="dense"
                tone={roles.justChanged.tagTone}
                dot
                count={renamed}
              >
                Renamed
              </Tag>
            </li>
          )}
        </ul>
        {(keepAll || actions) && (
          <div className="summary-actions" ref={absentList?.actionsRef}>
            {keepAll}
            {actions}
          </div>
        )}
      </Row>
      {/*
       * The summary's polite region (Block A): the late-refusal line (R12, reading 3), filled a
       * moment after the summary mounts so it is announced, and the list's *Kept {name}* /
       * *Kept {n}* / *{name} removed*. Always mounted, so what is put in it is announced.
       */}
      <Below>
        <LateRefusalStatus line={late}>
          <span style={visuallyHidden}>{absentList?.announced ?? ''}</span>
        </LateRefusalStatus>
        {absentList?.list}
      </Below>
    </Card>
  );
};

/** Where focus goes once a decided row has left the list (Block A). */
type FocusTarget = { keep: number } | 'head';
/**
 * The target, and whether focus is the summary's to move whatever holds it then: a Remove settles
 * while its own confirmation (outside the card) has focus, so it is.
 */
type FocusAfter = { target: FocusTarget; owned: boolean } | null;

/** Focus has gone nowhere: <body>, or a node no longer in the document. */
const focusLost = (active: Element | null): boolean =>
  !active || active === document.body || !active.isConnected;

/**
 * The summary with its *Not in this file* list (R6–R8, Block A). A component of its own because the
 * failure toast needs `useToast`, which only something mounted inside the page's `ToastProvider` can
 * reach — the plain summary needs none.
 */
const SummaryWithDecisions: React.FC<
  SummaryCardProps & { rows: readonly TwinAssetImportAbsentRow[]; decisions: AbsentDecisions }
> = ({ rows, decisions, ...card }) => {
  const toast = useToast();
  /*
   * Rows decided here, hidden at once rather than after the summary's re-read, so focus can move to
   * the next row the moment the server has said. A new read of the record (a new `rows` array)
   * starts again from what it says.
   */
  const [hidden, setHidden] = useState<ReadonlySet<number>>(new Set());
  const [keeping, setKeeping] = useState<ReadonlySet<number>>(new Set());
  const [keepingAll, setKeepingAll] = useState(false);
  const [announced, setAnnounced] = useState('');
  const [focusAfter, setFocusAfter] = useState<FocusAfter>(null);
  const keepButtons = useRef(new Map<number, HTMLButtonElement>());
  const actionsRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);
  /*
   * Same-tab Keeps run one after another: the server keeps by read-modify-write, so two at once
   * could each write the record without the other's row. The buttons stay usable; a press queues.
   */
  const chain = useRef<Promise<void>>(Promise.resolve());
  const mounted = useRef(true);
  // Set on every mount, not only cleared: StrictMode's development mount runs the cleanup once and
  // mounts again, and a flag left false would skip every settle (found on the CP7 staging run).
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setHidden(new Set());
  }, [rows]);

  const visible = rows.filter(row => !hidden.has(row.id));
  // The list as last drawn, for a decision that settles after later ones were pressed.
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const shown = visible.slice(0, ABSENT_LIST_LIMIT);
  const more = visible.length - shown.length;

  /** The row after `id` still in the list, else the one before, else the summary's head. */
  const nextFocus = (gone: ReadonlySet<number>): FocusTarget => {
    const list = visibleRef.current;
    const remaining = list.filter(row => !gone.has(row.id));
    if (remaining.length === 0) return 'head';
    const at = list.findIndex(row => gone.has(row.id));
    const after = list.slice(at + 1).find(row => !gone.has(row.id));
    return { keep: (after ?? remaining[remaining.length - 1]).id };
  };

  useEffect(() => {
    if (focusAfter === null) return;
    setFocusAfter(null);
    /*
     * Moved only while focus is still the summary's — inside the card (the Keep just pressed, or the
     * one that left with its row) — or lost. An operator who has moved on keeps their place; the
     * polite region says what happened (as `AssetCsvUploader`'s collapse repairs only focus it
     * destroys).
     */
    const card = titleRef.current?.closest('.equipment-landed-summary');
    const active = document.activeElement;
    if (!focusAfter.owned && !card?.contains(active) && !focusLost(active)) return;
    const { target } = focusAfter;
    if (target !== 'head') {
      keepButtons.current.get(target.keep)?.focus();
      return;
    }
    // *Undo This Import* when it is offered — the actions' button that is not *Keep All* — else
    // the summary's title.
    const undo = actionsRef.current?.querySelector<HTMLElement>('button:not([data-keep-all])');
    (undo ?? titleRef.current)?.focus();
  }, [focusAfter]);

  const settle = (gone: ReadonlySet<number>, sentence: string, owned = false) => {
    if (!mounted.current) return;
    setFocusAfter({ target: nextFocus(gone), owned });
    setHidden(previous => {
      const next = new Set(previous);
      gone.forEach(id => next.add(id));
      return next;
    });
    setAnnounced(sentence);
  };

  /** A Keep or Keep All the server refused: said assertively, with its sentence. */
  const failToast = (title: string, failure: unknown) =>
    toast.show({
      tone: 'error',
      icon: <Icon name="circle-exclamation" />,
      title,
      ...(failure instanceof Error && failure.message ? { detail: failure.message } : {}),
    });

  const keep = (row: TwinAssetImportAbsentRow) => {
    const label = rowLabel(row);
    setKeeping(previous => new Set(previous).add(row.id));
    chain.current = chain.current.then(async () => {
      try {
        await decisions.keep({ ids: [row.id] });
        settle(new Set([row.id]), `Kept ${label}`);
      } catch (failure) {
        failToast(`Couldn’t keep ${label}`, failure);
      } finally {
        if (mounted.current) {
          setKeeping(previous => {
            const next = new Set(previous);
            next.delete(row.id);
            return next;
          });
        }
      }
    });
  };

  const keepAll = () => {
    const ids = new Set(visible.map(row => row.id));
    setKeepingAll(true);
    chain.current = chain.current.then(async () => {
      try {
        await decisions.keep({ all: true });
        settle(ids, `Kept ${ids.size}`);
      } catch (failure) {
        failToast('Couldn’t keep them', failure);
      } finally {
        if (mounted.current) setKeepingAll(false);
      }
    });
  };

  const removed = (row: TwinAssetImportAbsentRow, notice: string) => {
    // Settled from the Remove's own confirmation, which holds focus until it closes: the
    // summary's to move.
    settle(new Set([row.id]), `${rowLabel(row)} removed`, true);
    decisions.onRemoved(notice);
  };

  const refused = (row: TwinAssetImportAbsentRow, message: string) =>
    toast.show({
      tone: 'error',
      icon: <Icon name="circle-exclamation" />,
      title: `Couldn’t remove ${rowLabel(row)}`,
      detail: message,
    });

  // With nothing undecided, no list and no *Keep All* (R6).
  const view: AbsentListView = {
    actionsRef,
    titleRef,
    announced,
    keepAll:
      visible.length === 0 ? null : (
        <Button
          type="button"
          variant="frosted"
          size="sm"
          data-keep-all=""
          iconLeft={<Icon name="check" size="sm" />}
          aria-label={`Keep all ${visible.length} not in this file`}
          loading={keepingAll}
          onClick={keepAll}
        >
          {keepingAll ? 'Keeping…' : `Keep All ${visible.length}`}
        </Button>
      ),
    list:
      visible.length === 0 ? null : (
        <>
          <ul className="absent-list" aria-label="Not in this file">
            {shown.map(row => {
              const label = rowLabel(row);
              const where = whereText(decisions.removal, row.id);
              const pressed = keeping.has(row.id);
              return (
                <li key={row.id} className="absent-row">
                  <p className="absent-text">
                    <span className="absent-name">{orDash(row.name)}</span>
                    {` · ${orDash(row.identifier)}`}
                    {where ? ` · ${where}` : ''}
                  </p>
                  <span className="absent-actions">
                    <Button
                      ref={(node: HTMLButtonElement | null) => {
                        if (node) keepButtons.current.set(row.id, node);
                        else keepButtons.current.delete(row.id);
                      }}
                      type="button"
                      variant="frosted"
                      size="xs"
                      aria-label={`Keep ${label}`}
                      loading={pressed}
                      disabled={keepingAll}
                      onClick={() => keep(row)}
                    >
                      {pressed ? 'Keeping…' : 'Keep'}
                    </Button>
                    <EquipmentAssetRemove
                      rowId={row.id}
                      name={label}
                      removal={decisions.removal}
                      trigger="row"
                      // Not while this row's Keep is pending or queued: one decision a row.
                      disabled={keepingAll || pressed}
                      onRefused={message => refused(row, message)}
                      onRemoved={notice => removed(row, notice)}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
          {more > 0 && (
            <p className="absent-more">{`and ${more} more not shown — Keep All keeps them too`}</p>
          )}
        </>
      ),
  };

  return <SummaryCard {...card} absentList={view} />;
};

export const EquipmentLandedSummary: React.FC<EquipmentLandedSummaryProps> = ({
  absent,
  ...card
}) => {
  /*
   * The rows nobody has decided on yet. The middleware has already dropped any no longer on the
   * register (R10), so a removed row leaves with the summary's next read.
   */
  const source = card.record.absent;
  const rows = useMemo(() => (source ?? []).filter(row => !row.kept), [source]);
  if (!absent) return <SummaryCard {...card} />;
  return <SummaryWithDecisions {...card} rows={rows} decisions={absent} />;
};

export default EquipmentLandedSummary;
